import { trackSearchSummaryEvent } from '@modules/feed/utils/trackSearchSummaryEvents';
import { GenerateSummaryLoader } from '@modules/oracle/components/GenerateSummaryLoader';
import { FeedbackSummaryStoreProvider } from '@modules/oracle/feedbackSummaryStore/FeedbackSummaryStoreProvider';
import { useFeedbackSummaryStore } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryStore';
import { useGetFeedbackListSummary } from '@modules/oracle/hooks';
import { Box, Skeleton, Slide, Stack } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { FieldNames } from 'v2/core/entities/fields';
import { QueryContainer } from 'v2/core/services/notebook/types';
import useQueryInspector from 'v2/core/services/queryBuilder/useQueryInspector';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { GenerateSearchSummaryView } from '../GenerateSearchSummaryView';
import { SearchSummaryContent } from '../SearchSummaryContent';
import { SearchSummaryHeader } from '../SearchSummaryHeader';

interface SearchSummaryProps {
  queryContainer: QueryContainer;
}

export const SearchSummaryContainer = ({
  queryContainer,
}: SearchSummaryProps) => {
  const {
    responseID,
    getFeedbackSummary,
    sessionID: isSocketConnectionEstablished,
  } = useGetFeedbackListSummary({
    queryContainer,
    shouldGetSummaryOnDemand: true,
  });

  const reasonFilters = useQueryInspector(
    queryContainer.WhereClause ?? EmptyWhereClause,
    FieldNames.Reason,
  );

  const [isGenerateActionPerformed, setIsGenerateActionPerformed] =
    useState(false);
  const slideContainerRef = useRef<HTMLDivElement>(null);

  const messages = useFeedbackSummaryStore(({ data }) => data.messages);
  const isCompleted = useFeedbackSummaryStore(
    ({ visual }) => visual.isCompleted,
  );

  const generateSummary = () => {
    setIsGenerateActionPerformed(true);
    getFeedbackSummary();
  };

  const onGenerateSummaryClick = () => {
    generateSummary();
    trackSearchSummaryEvent({
      eventName: 'Search Summary - Generate Summary',
    });
  };

  const hasNoSummary = messages.length === 0 && isCompleted;

  const renderSummaryContent = useCallback(() => {
    if (!isCompleted && messages.length === 0) {
      return <GenerateSummaryLoader />;
    }

    return <SearchSummaryContent hasNoSummary={hasNoSummary} />;
  }, [hasNoSummary, isCompleted, messages.length]);

  if (!isSocketConnectionEstablished) {
    return <Skeleton variant="rounded" width="100%" height={36.5} />;
  }

  if (!isGenerateActionPerformed) {
    return (
      <GenerateSearchSummaryView
        onGenerateSummaryClick={onGenerateSummaryClick}
        shouldShowCooccurringDataPoints={reasonFilters.length > 0}
      />
    );
  }

  return (
    <Stack ref={slideContainerRef} overflow="hidden">
      <SearchSummaryHeader
        queryContainer={queryContainer}
        hasNoSummary={hasNoSummary}
        responseID={responseID}
      />
      <Slide
        direction="down"
        in
        timeout={500}
        container={slideContainerRef.current}
      >
        <Box>{renderSummaryContent()}</Box>
      </Slide>
    </Stack>
  );
};

export const SearchSummary = ({ queryContainer }: SearchSummaryProps) => {
  return (
    <FeedbackSummaryStoreProvider>
      <SearchSummaryContainer queryContainer={queryContainer} />
    </FeedbackSummaryStoreProvider>
  );
};
